import { LightSourceData } from '@common/components/components';

/**
 * Represents a light source at a world position for lighting calculations.
 */
export interface LightSourceInfo {
  x: number;
  y: number;
  strength: number;
  distance: number;
  color: number;
  colorAlpha: number;
}

/**
 * Result of lighting calculation for a position.
 */
export interface LightResult {
  /** Light level 0-15 */
  level: number;
  /** Dominant light color (hex) */
  color: number;
  /** Color intensity 0-1 */
  colorAlpha: number;
}

/**
 * Service for managing the client-side lighting grid.
 *
 * Tracks light levels per tile (configurable resolution).
 * Combines ambient light with light sources to produce final values.
 */
export class LightingService {
  /** Light tiles per map tile (1 = same resolution as map) */
  public lightTileScale = 1;

  /** Default tile size in pixels (standard map tile) */
  private baseTileSize = 16;

  /** Size of each light tile in pixels */
  public get lightTileSize(): number {
    return this.baseTileSize / this.lightTileScale;
  }

  /** Lighting enabled flag */
  public enabled = true;

  /** Current ambient light level (0-1), updated from GameTimeService */
  public ambientLight = 1.0;

  /**
   * Set the base tile size (from map metadata).
   */
  public setBaseTileSize(size: number): void {
    this.baseTileSize = size;
  }

  /**
   * Calculate light value for a world position.
   * Combines ambient light with nearby light sources.
   *
   * @param worldX - X position in world coordinates (pixels)
   * @param worldY - Y position in world coordinates (pixels)
   * @param lightSources - Array of light sources to consider
   * @returns Light result with level (0-15) and color
   */
  public calculateLightAtPosition(
    worldX: number,
    worldY: number,
    lightSources: LightSourceInfo[],
  ): LightResult {
    // Ambient light scaled to 0-15 range (same as light source strength)
    let maxLevel = this.ambientLight * 15;
    let resultColor = 0xffffff;
    let resultAlpha = 1;

    for (const source of lightSources) {
      const dx = worldX - source.x;
      const dy = worldY - source.y;
      const distSquared = dx * dx + dy * dy;
      const maxDistSquared = source.distance * source.distance;

      // Quick reject: skip sqrt if clearly outside range
      if (distSquared < maxDistSquared) {
        const dist = Math.sqrt(distSquared);
        // Linear falloff from full strength at center to 0 at edge
        const falloff = 1 - dist / source.distance;
        const contribution = source.strength * falloff;

        // Take the brightest light source (both ambient and strength are 0-15 scale)
        if (contribution > maxLevel) {
          maxLevel = contribution;
          resultColor = source.color;
          resultAlpha = source.colorAlpha;
        }
      }
    }

    return {
      level: Math.min(15, maxLevel),
      color: resultColor,
      colorAlpha: resultAlpha,
    };
  }

  /**
   * Convert a LightSourceData component to LightSourceInfo for calculations.
   */
  public componentToInfo(data: LightSourceData, x: number, y: number): LightSourceInfo {
    return {
      x,
      y,
      strength: data.strength,
      distance: data.distance,
      color: data.color,
      colorAlpha: data.colorAlpha,
    };
  }
}
